/**
 * CountingPrint - Figma-matching print renderer for counting quizzes
 * Layout: PrintQuizTitle → image → number input boxes (56x56, gap-20px, centered)
 */

import { cn } from '../../../lib/utils'
import { KatexText } from '../shared'
import type { CountingQuizData } from '../../../types/legacyQuiz'
import { PRINT_CORRECT_ANSWER_CLASS, PrintQuizTitle, PrintQuizProblem, PrintQuizImage, PrintInputBox, getStyles, getImageUrl, getPrintScale } from './print-common'

interface PrintQuizProps {
  quizId: string | number
  quiz: CountingQuizData
  number: number
  showCorrectAnswers?: boolean
  correctAnswers?: string[]
  compact?: boolean
}

export function CountingPrint({
  quizId,
  quiz,
  number,
  showCorrectAnswers = false,
  correctAnswers,
  compact,
}: PrintQuizProps) {
  const s = getStyles(compact)
  const numDigits = quiz.digits || 1
  const imageUrl = getImageUrl(quiz.image, quiz.imagePrint)
  const printScale = getPrintScale(quiz.image, quiz.imagePrint)

  // Parse correct digits from first answer string
  const correctDigits: number[] | null = (() => {
    if (!showCorrectAnswers || !correctAnswers?.[0]) return null
    return correctAnswers[0].split('').map(Number)
  })()

  return (
    <div className={cn(s.container, s.quizSection)}>
      {/* Title */}
      <PrintQuizTitle number={number} compact={compact}>
        <KatexText text={quiz.title} />
      </PrintQuizTitle>

      {/* Problem body (with {image=N} → additionalImages) */}
      <PrintQuizProblem
        problem={quiz.problem}
        quizId={quizId}
        additionalImages={quiz.additionalImages}
        compact={compact}
        stripBlanks
      />

      {/* Image */}
      {imageUrl && <PrintQuizImage src={imageUrl} printScale={printScale} />}

      {/* Number input boxes */}
      <div className={cn('flex items-center justify-center w-full', compact ? 'gap-[10px]' : 'gap-[20px]')}>
        {Array.from({ length: numDigits }).map((_, index) => {
          const correctDigit = correctDigits?.[index]
          const showDigit = showCorrectAnswers && correctDigit != null

          return (
            <PrintInputBox
              key={index}
              size="md"
              compact={compact}
              className={showDigit ? `text-[20px] ${PRINT_CORRECT_ANSWER_CLASS}` : ''}
            >
              {showDigit ? correctDigit : '\u00A0'}
            </PrintInputBox>
          )
        })}
      </div>
    </div>
  )
}
